import { createContext, useCallback, useEffect, useState } from "react";

import AuthService from "@services/api/auth";
import ProfileService from "@services/api/profile";
import CalendarService from "@services/api/calendar";
import OrganizationService from "@services/api/organization";
import LocalStorageService from "@services/localStorage";
import RolesGraphqlInstance from "@services/api/roles.graphql";
import { ability, convertAbility } from "ability/ability";

import {
  AFTER_SURVEY_STEPS,
  SURVEY_STATUSES,
  USER_ROLES,
  MEDIA_FONT_SIZE,
  CUSTOM_USER_ROLES,
} from "@utils/consts";
import calendarGraphqlInstance from "@services/api/calendar.graphql";
import { useLocation } from "react-router-dom";
import AccountGraphqlInstance from "@services/api/account.graphql";
import { APPOINTMENTS, ORGANIZATION, PROFILE, SETTINGS } from "@router/consts";

export const UserContext = createContext({
  user: null,
  profile: null,
  organization: null,
  surveyStatus: null,
  userCalendar: null,
  htmlFontSize: null,
  isMainLoading: true,
  login: async () => {},
  logout: async () => {},
  register: async () => {},
  registerConsumer: async () => {},
  forgotPassword: async () => {},
  resetPassword: async () => {},
  checkIsSSO: async () => {},
  setHtmlFontSize: () => {},
  distributedLogin: async () => {},
  switchAccountTokens: async () => {},
  setSurveyStatus: async () => {},
  setShowBroadcastModal: () => {},
  handleAccountSwitch: () => {},
  setProfile: () => {},
});

const UserProvider = ({ children }) => {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [surveyStatus, setSurveyStatus] = useState(null);
  const [userCalendar, setUserCalendar] = useState(null);
  const [afterSurveyWrapStep, setAfterSurveyWrapStep] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [htmlFontSize, setHtmlFontSize] = useState(MEDIA_FONT_SIZE.BASE);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [currentUserRoots, setCurrentUserRoots] = useState(null);
  const [isAllAccountsLogin, setIsAllAccountsLogin] = useState(false);
  const [isMainLoading, setIsMainLoading] = useState(true);

  const setUserContextData = useCallback(
    async (userData, isRegistration = null, showSurvey = true) => {
      setIsAllAccountsLogin(userData?.accountIds?.length > 1);
      if (location.pathname?.includes("/sign-up-consumer")) {
        setUser(userData);
        return;
      }

      const profileResponse = await ProfileService.getProfileByUserId(
        userData.accountId,
        userData.id,
      );

      if (
        profileResponse?.type !== USER_ROLES.STUDENT_ROLE &&
        profileResponse?.type !== USER_ROLES.COUNSELLOR_ROLE &&
        profileResponse?.type !== USER_ROLES.PRINCIPAL_ROLE &&
        profileResponse?.type !== USER_ROLES.ALLBRY_COUNSELLOR_ROLE
      ) {
        LocalStorageService.removeAccessToken();
        LocalStorageService.removeRefreshToken();
        throw new Error();
      }

      if (isRegistration) {
        switch (profileResponse?.type) {
          case USER_ROLES.STUDENT_ROLE:
            if (showSurvey) {
              LocalStorageService.setSurveyStatus(
                SURVEY_STATUSES.STUDENT_SURVEY,
              );
              setSurveyStatus(SURVEY_STATUSES.STUDENT_SURVEY);
            } else {
              setAfterSurveyWrapStep(AFTER_SURVEY_STEPS.FIRST_STEP);
            }
            break;
          case USER_ROLES.COUNSELLOR_ROLE:
            setAfterSurveyWrapStep(AFTER_SURVEY_STEPS.FIRST_STEP);
            break;
          default:
            break;
        }
      } else if (isRegistration === false) {
        if (
          profileResponse?.gender === "" ||
          profileResponse?.gender === null
        ) {
          LocalStorageService.setSurveyStatus(SURVEY_STATUSES.STUDENT_GENDER);
          setSurveyStatus(SURVEY_STATUSES.STUDENT_GENDER);
        }
      }

      const userCalendarResponse = await CalendarService.getCalendarByUserId(
        userData.accountId,
        userData.id,
      );

      const organizationResponse =
        await OrganizationService.getOrganizationById(
          userData.accountId,
          profileResponse.organizationId,
        );

      // Set roles and permissions
      try {
        const data = await RolesGraphqlInstance.getRoleFeaturesByCurrentUser();
        if (data) {
          const rules = convertAbility(data || []);
          ability.update(rules);
        }
      } catch (err) {}

      setUser(userData);
      setUserCalendar(
        userData?.accountIds?.length > 1
          ? userCalendarResponse
          : userCalendarResponse[0],
      );
      setOrganization(organizationResponse);
      setProfile(profileResponse);
    },
    [],
  );

  const setUserInitData = useCallback(async () => {
    setIsMainLoading(true);

    const accessToken = LocalStorageService.getAccessToken();
    const refreshToken = LocalStorageService.getRefreshToken();

    if (!accessToken && !refreshToken) {
      setIsMainLoading(false);
      return;
    }

    const localSurveyStatus = LocalStorageService.getSurveyStatus();

    if (localSurveyStatus && accessToken) {
      setSurveyStatus(localSurveyStatus);
    }

    try {
      let userResponse;

      try {
        userResponse = await AuthService.getCurrentUser();
      } catch (err) {
        // Try to refresh token if current one is invalid
        const result = await AuthService.refreshToken();

        if (result) {
          LocalStorageService.setAccessToken(result.accessToken);
          LocalStorageService.setRefreshToken(result.refreshToken);

          // Current user after token refresh
          userResponse = await AuthService.getCurrentUser();
        }
      }
      await setUserContextData(userResponse);
    } catch (err) {
      LocalStorageService.removeAccessToken();
      LocalStorageService.removeRefreshToken();
      LocalStorageService.removeAppCache();
    } finally {
      setIsMainLoading(false);
    }
  }, [setUserContextData]);

  useEffect(() => {
    setUserInitData();

    // This is needed to track local storage changes from other browser tab
    // (changes from the tab listener was created in are not triggering event)
    const storageHandler = (e) => {
      // local storage key
      if (e.key === "accessToken") {
        setUserInitData();
      }
    };

    window.addEventListener("storage", storageHandler);

    return () => {
      window.removeEventListener("storage", storageHandler);
    };
  }, [setUserInitData]);

  const login = useCallback(async (email, password) => {
    const result = await AuthService.login(email, password);
    if (!result.accessToken) {
      if (result?.userId) {
        return result;
      }

      if (result.isMissingPassword) {
        return false;
      }
    }

    if (result.redirectUrl || !result.accessToken) {
      return true;
    }

    if (result.redirectUrl || !result.accessToken) {
      return true;
    }

    LocalStorageService.setAccessToken(result.accessToken);
    LocalStorageService.setRefreshToken(result.refreshToken);

    const userResponse = await AuthService.getCurrentUser();
    await setUserContextData(userResponse, false);

    return true;
  }, []);

  const setTokens = useCallback(async (tokens) => {
    LocalStorageService.setAccessToken(tokens.accessToken);
    LocalStorageService.setRefreshToken(tokens.refreshToken);

    const userResponse = await AuthService.getCurrentUser();
    await setUserContextData(userResponse, false);
  }, []);

  const register = useCallback(async (email, password) => {
    const result = await AuthService.emailRegistration(email, password);

    LocalStorageService.setAccessToken(result.accessToken);
    LocalStorageService.setRefreshToken(result.refreshToken);

    const userResponse = await AuthService.getCurrentUser();
    await setUserContextData(userResponse, true);
  }, []);

  const registerConsumer = useCallback(async (accessToken, refreshToken) => {
    LocalStorageService.setAccessToken(accessToken);
    LocalStorageService.setRefreshToken(refreshToken);

    const userResponse = await AuthService.getCurrentUser();
    await setUserContextData(userResponse, true, false);
  }, []);

  const checkIsSSO = useCallback(async (email, isRegistration = false) => {
    const isSSO = await AuthService.isSSO(email, isRegistration);
    return isSSO;
  }, []);

  const forgotPassword = useCallback(async (email) => {
    const result = await AuthService.forgotPassword(email);
    return result;
  }, []);

  const resetPassword = useCallback(async (newPassword, token) => {
    const result = await AuthService.resetPassword(newPassword, token);

    LocalStorageService.setAccessToken(result.accessToken);
    LocalStorageService.setRefreshToken(result.refreshToken);

    const userResponse = await AuthService.getCurrentUser();
    await setUserContextData(userResponse);
  }, []);

  const logout = useCallback(async () => {
    await AuthService.logout();

    LocalStorageService.removeAccessToken();
    LocalStorageService.removeRefreshToken();
    LocalStorageService.removeAppCache();

    // Order is very important
    setProfile(null);
    setUser(null);
    setUserCalendar(null);
    setOrganization(null);
    setAfterSurveyWrapStep(null);
  }, []);

  const completeSurvey = () => {
    setSurveyStatus(null);
    LocalStorageService.removeSurveyStatus();
    setAfterSurveyWrapStep(AFTER_SURVEY_STEPS.FIRST_STEP);
  };

  const updateCurrentProfile = useCallback(
    async (profileData) => {
      const newProfile = {
        ...profile,
        ...profileData,
      };
      const updatedProfile = await ProfileService.updateProfileById(
        profile.accountId,
        profile.id,
        newProfile,
      );
      setProfile((prevProfile) => ({ ...prevProfile, ...updatedProfile }));
    },
    [profile],
  );

  const updateCalendarSyncStatus = useCallback(
    async (status) => {
      const updatedCalender =
        await calendarGraphqlInstance.updateCalendarSyncStatus(status);
      setUserCalendar(updatedCalender);
    },
    [userCalendar],
  );

  const updateCalendarSyncFreeTimeStatus = useCallback(
    async (status) => {
      const updatedCalender =
        await calendarGraphqlInstance.updateCalendarSyncFreeTimeStatus(status);
      setUserCalendar(updatedCalender);
    },
    [userCalendar],
  );

  const updateOrganization = useCallback(async (form) => {
    const newOrganization = await OrganizationService.updateOrganization(
      organization.accountId,
      organization.id,
      {
        ...form,
        id: organization.id,
        name: organization.name,
        accountId: organization.accountId,
      },
    );
    setOrganization(newOrganization);
  });

  const switchAccountTokens = useCallback(async (accountIds) => {
    const result = await AuthService.switchAccount(accountIds);

    LocalStorageService.setAccessToken(result.accessToken);
    LocalStorageService.setRefreshToken(result.refreshToken);
  });

  const distributedLogin = useCallback(async (accountIds) => {
    await switchAccountTokens(accountIds);

    try {
      const userResponse = await AuthService.getCurrentUser();
      await setUserContextData(userResponse);
    } catch (e) {
      LocalStorageService.removeAccessToken();
      LocalStorageService.removeRefreshToken();
      LocalStorageService.removeAppCache();

      setProfile(null);
      setUser(null);
      setUserCalendar(null);
      setOrganization(null);
    }
  }, []);

  const setUserRootsCall = useCallback((roots) => {
    setCurrentUserRoots(roots);
  });

  const handleAccountSwitch = useCallback(
    async (accountId) => {
      if (!user) {
        return;
      }

      try {
        // Check if the user is an Allbry Counsellor
        const isAllbryCounsellor = user.roles.some(
          (role) => role.name === USER_ROLES.ALLBRY_COUNSELLOR_ROLE,
        );

        if (isAllbryCounsellor) {
          await distributedLogin(accountId);
        } else {
          // Update session for the account and handle redirection based on roles
          await AccountGraphqlInstance.updateAuthUserSession(accountId);
        }

        // After login or session update, fetch and update user role features
        const roleFeaturesData =
          await RolesGraphqlInstance.getRoleFeaturesByCurrentUser();
        if (roleFeaturesData) {
          const rules = convertAbility(roleFeaturesData || []);
          ability.update(rules);
        }

        // Set the redirection after role features are updated
        setTimeout(() => {
          window.location.replace(
            ability.can(
              CUSTOM_USER_ROLES[USER_ROLES.COUNSELLOR_ROLE].APPOINTMENTS,
              USER_ROLES.COUNSELLOR_ROLE,
            ) ||
              ability.can(
                CUSTOM_USER_ROLES[USER_ROLES.STUDENT_ROLE].APPOINTMENTS,
                USER_ROLES.STUDENT_ROLE,
              )
              ? APPOINTMENTS.toString()
              : SETTINGS,
          );
        });
      } catch (err) {
        console.error("Error during account switch:", err);
        // In case of an error, fall back to the default redirection logic
        window.location.replace(
          ability.can(
            CUSTOM_USER_ROLES[USER_ROLES.COUNSELLOR_ROLE].APPOINTMENTS,
            USER_ROLES.COUNSELLOR_ROLE,
          ) ||
            ability.can(
              CUSTOM_USER_ROLES[USER_ROLES.STUDENT_ROLE].APPOINTMENTS,
              USER_ROLES.STUDENT_ROLE,
            )
            ? APPOINTMENTS.toString()
            : SETTINGS,
        );
      }
    },
    [user],
  );

  const value = {
    user,
    isMainLoading,
    profile,
    userCalendar,
    organization,
    surveyStatus,
    completeSurvey,
    login,
    logout,
    register,
    registerConsumer,
    forgotPassword,
    resetPassword,
    updateCurrentProfile,
    afterSurveyWrapStep,
    setAfterSurveyWrapStep,
    setTokens,
    checkIsSSO,
    updateOrganization,
    htmlFontSize,
    setHtmlFontSize,
    distributedLogin,
    switchAccountTokens,
    setSurveyStatus,
    updateCalendarSyncStatus,
    setShowBroadcastModal,
    showBroadcastModal,
    setUserRootsCall,
    currentUserRoots,
    updateCalendarSyncFreeTimeStatus,
    isAllAccountsLogin,
    handleAccountSwitch,
    setProfile,
  };

  return (
    <UserContext.Provider value={{ ...value }}>{children}</UserContext.Provider>
  );
};

export default UserProvider;
